// @workflow tasks.show-in-3d
/**
 * Task Isolation Utilities
 *
 * Shared utility functions for cross-asset tag isolation from Task views.
 * These functions handle the logic for determining which facilities a task's
 * linked tags are available in, and how to navigate to them.
 *
 * @see TagCard.vue for the reference implementation in the Tags feature
 */

import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { resolveTaskSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigation';
import { TAG_LINK_ACTION, tagLinkQuery } from '@/utils/tagDeepLink';

// Per-tag-set cache (legacy) — keyed by sorted tag-id fingerprint. Populated
// by the per-task fetch fallback path; still consulted by getTaskIsolationInfo
// for back-compat with standalone TaskIsolateButton usage outside boards.
const isolationCache = new Map();

// Per-task-id map populated by prefetchTasksIsolation. The Task Board batches
// every visible task's isolation in a single API call and writes results here;
// getTaskIsolationInfo reads from this map first to avoid any per-task fetch.
const taskIsolationByTaskId = new Map();

// Tracks the most recent in-flight prefetch promise so consumers (e.g.,
// TaskIsolateButton.ensureDataFetched) can wait for the batch to resolve
// before checking the map. Without this, a card mount that happens during
// prefetch would race-fall-back to the legacy per-task path.
let currentPrefetchPromise = null;

// The isolation caches above hold facility-RELATIVE results: otherAssets,
// isHarvestedInCurrentFacility, isPositionedInCurrentFacility are all computed
// against the active facility (server-side via GET_TASKS_ISOLATION_BY_IDS with
// assetId, or client-side vs store.state.assetId). But the caches are keyed by
// task/tag id ONLY — so an in-app facility switch (A → B) with the same task
// ids would reuse facility A's result on B, making a cross-facility task look
// local: no orange isolate button, and auto-isolation targets the wrong viewer.
// Track the facility the caches were built for and drop them when it changes.
// (A full page refresh recreates this module → null → fresh fetch, which is why
// refreshing "fixes it".)
let cachedForAssetId = null;

function normalizeTaskIsolationEntry(entry, task = null) {
  const directTagIds = Array.isArray(task?.forgeTagsIds) ? task.forgeTagsIds : [];
  const serverTagIds = Array.isArray(entry?.effectiveTagIds) ? entry.effectiveTagIds : [];
  return {
    combinedAssets: entry?.combinedAssets || [],
    otherAssets: entry?.otherAssets || [],
    hasCurrentFacility: !!entry?.hasCurrentFacility,
    harvestedAssets: entry?.harvestedAssets || [],
    positionedAssets: entry?.positionedAssets || [],
    isHarvestedInCurrentFacility: !!entry?.isHarvestedInCurrentFacility,
    isPositionedInCurrentFacility: !!entry?.isPositionedInCurrentFacility,
    effectiveTagIds: [...new Set(serverTagIds.length ? serverTagIds : directTagIds)],
    targets: Array.isArray(entry?.targets) ? entry.targets : [],
  };
}

function summarySpatialTarget(facilityId, viewer, hasModelElements) {
  return {
    facilityId,
    viewer,
    provenance: viewer === '360' ? 'facility-scan-position' : 'model-elements',
    image360Id: null,
    hasPoint: true,
    hasModelElements,
  };
}

function taskSummarySpatialTargets(isolationInfo) {
  const harvested = isolationInfo?.harvestedAssets || [];
  const positioned = isolationInfo?.positionedAssets || [];
  return [
    ...harvested.map((facilityId) => summarySpatialTarget(facilityId, '3d', true)),
    ...positioned.map((facilityId) => summarySpatialTarget(
      facilityId,
      '360',
      harvested.includes(facilityId),
    )),
  ];
}

function taskResolutionTags(isolationInfo, fallbackTagIds = []) {
  if (Array.isArray(isolationInfo?.tags) && isolationInfo.tags.length > 0) {
    return isolationInfo.tags;
  }
  const tagIds = isolationInfo?.effectiveTagIds?.length
    ? isolationInfo.effectiveTagIds
    : fallbackTagIds;
  const targets = taskSummarySpatialTargets(isolationInfo);
  return [...new Set(tagIds || [])].filter(Boolean).map((id) => ({
    id,
    spatialTargets: targets,
  }));
}

function planTargetsFacility(plan, facilityId) {
  return (plan?.kind === 'viewer' || plan?.kind === 'navigate-facility')
    && String(plan.facilityId).toLowerCase() === String(facilityId).toLowerCase();
}

function taskLinkIcon(plan, fallbackInfo, facilityId) {
  if (planTargetsFacility(plan, facilityId)) {
    return plan.viewer === '360'
      ? 'mdi-image-filter-center-focus'
      : 'mdi-cube-outline';
  }
  return getCopyLinkItemIcon(fallbackInfo, facilityId);
}

/**
 * Effective context tags for local/cross-facility isolation. New API results
 * own this decision; direct forgeTagsIds remain the rolling-deploy fallback.
 */
export function getTaskEffectiveTagIds(task, isolationInfo = null) {
  const effective = isolationInfo?.effectiveTagIds;
  if (Array.isArray(effective) && effective.length) return [...new Set(effective)];
  return [...new Set(Array.isArray(task?.forgeTagsIds) ? task.forgeTagsIds : [])];
}

/**
 * Flat exact-target dbId union for a normalized task isolation result.
 */
export function getTaskIsolationTargetDbIds(isolationInfo) {
  return [...new Set(
    (isolationInfo?.targets || [])
      .flatMap((target) => target?.forgeElementDbIds || [])
      .filter((dbId) => dbId !== null && dbId !== undefined),
  )];
}

/**
 * Fetches and combines isolation info from all tags linked to a task.
 *
 * @param {Object} task - Task object with forgeTagsIds array
 * @param {Object} store - Vuex store instance
 * @param {boolean} useCache - Whether to use cached results (default: true)
 * @returns {Promise<Object|null>} Isolation info or null if no tags
 *
 * @example
 * const info = await getTaskIsolationInfo(task, this.$store);
 * // Returns: { combinedAssets, otherAssets, hasCurrentFacility, tags }
 */
export async function getTaskIsolationInfo(task, store, useCache = true) {
  if (!task?.id && !task?.forgeTagsIds?.length) {
    return null;
  }

  // Fast path 1 — board-batched prefetch already wrote our task into the map.
  // Avoids any per-task network call. This is what Phase 2 optimizes for: at
  // 2K-5K tasks, every Task Board load goes through prefetchTasksIsolation
  // exactly once, populating this map in a single API round-trip.
  if (useCache && task?.id && taskIsolationByTaskId.has(task.id)) {
    return taskIsolationByTaskId.get(task.id);
  }

  // Persisted tasks resolve through the same batched endpoint even when this
  // standalone caller mounted outside a board prefetch. This is the only path
  // that can see TaskMonitoringScopes and anomaly ComponentRef contexts.
  if (task?.id) {
    try {
      const results = await store.dispatch('TASK/GET_TASKS_ISOLATION_BY_IDS', {
        taskIds: [task.id],
        assetId: store.state?.assetId || null,
      });
      const entry = Array.isArray(results)
        ? results.find((item) => item?.taskId === task.id)
        : null;
      if (entry) {
        const normalized = normalizeTaskIsolationEntry(entry, task);
        taskIsolationByTaskId.set(task.id, normalized);
        return normalized;
      }
    } catch (err) {
      debugError('TaskIsolation', 'Task isolation endpoint fallback failed:', err);
      // A direct-tag task can still use the legacy tag fetch below. A
      // scope-only task has no safe client-side source and returns null.
    }
  }

  if (!task?.forgeTagsIds?.length) return null;

  // Fast path 2 — legacy per-tag-set cache. Survives across tasks that share
  // the exact same tag set within a single session.
  const cacheKey = [...task.forgeTagsIds].sort().join(',');
  if (useCache && isolationCache.has(cacheKey)) {
    return isolationCache.get(cacheKey);
  }

  // Fallback: standalone usage outside a board (no batched prefetch ran).
  // Stays as a defensive path so a single TaskIsolateButton mounted in
  // isolation still functions; expected to be cold rarely in practice.
  debugLog('TaskIsolation', 'Falling back to per-task fetch for task:', task?.id);

  try {
    // Fetch tags with full data (including children and availableAssets)
    const tags = await store.dispatch('GET_BASE_TAGS_BY_IDS', task.forgeTagsIds);

    if (!tags || !tags.length) {
      debugLog('TaskIsolation', 'No tags returned for IDs:', task.forgeTagsIds);
      return null;
    }

    const currentAssetId = store.state.assetId;

    // Build per-source sets so the result shape matches the batched API path
    // (HarvestedAssets / PositionedAssets / IsHarvested... / IsPositioned...).
    // Without these, the icon-decision helper (getIsolateIcon) can't tell 3D
    // from 360 in the fallback path and would default everywhere to mdi-loupe.
    const harvestedSet = new Set();
    const positionedSet = new Set();
    tags.forEach((tag) => {
      (tag.availableAssets || []).forEach((id) => id && harvestedSet.add(id));
      (tag.positionedFacilityIds || []).forEach((id) => id && positionedSet.add(id));
    });

    const harvestedAssets = [...harvestedSet];
    const positionedAssets = [...positionedSet];
    const combinedAssets = [...new Set([...harvestedSet, ...positionedSet])];
    const otherAssets = combinedAssets.filter((id) => id !== currentAssetId);

    // The "or any tag.isHarvestedInCurrentFacility" branch covers the
    // facility-agnostic harvest case (children with null ForgeAssetId) — the
    // server expresses the same idea via the IsHarvestedInCurrentFacility flag.
    const isHarvestedInCurrentFacility =
      !!currentAssetId &&
      (harvestedSet.has(currentAssetId) || tags.some((t) => t.isHarvestedInCurrentFacility));

    const isPositionedInCurrentFacility = !!currentAssetId && positionedSet.has(currentAssetId);

    const hasCurrentFacility = isHarvestedInCurrentFacility || isPositionedInCurrentFacility;

    const result = {
      combinedAssets,
      otherAssets,
      hasCurrentFacility,
      harvestedAssets,
      positionedAssets,
      isHarvestedInCurrentFacility,
      isPositionedInCurrentFacility,
      effectiveTagIds: [...task.forgeTagsIds],
      targets: [],
      tags,
    };

    // Cache the result
    isolationCache.set(cacheKey, result);

    // Debug logging to trace data flow
    debugLog(
      'TaskIsolation',
      'Tags returned:',
      tags.map((t) => ({
        id: t.id,
        type: t.discriminator || t.constructor?.name,
        availableAssets: t.availableAssets,
        childCount: t.children?.length,
      })),
    );
    debugLog('TaskIsolation', 'Current asset ID:', currentAssetId);
    debugLog('TaskIsolation', 'Combined assets:', combinedAssets);
    debugLog('TaskIsolation', 'Other assets (excluding current):', otherAssets);
    debugLog('TaskIsolation', 'Has current facility:', hasCurrentFacility);
    debugLog('TaskIsolation', 'Computed isolation info:', {
      tagCount: tags.length,
      combinedAssets: combinedAssets.length,
      otherAssets: otherAssets.length,
      hasCurrentFacility,
    });

    return result;
  } catch (error) {
    debugError('TaskIsolation', 'Failed to fetch tag isolation info:', error);
    return null;
  }
}

/**
 * Batch-fetch isolation info for every task on a board in a single API call.
 *
 * This is the Phase 2 optimization that replaces the per-task / per-tag-set
 * fetch pattern. The backend (POST /api/tasks/getIsolationByIds) does the
 * server-side aggregation — we just dispatch and index.
 *
 * Idempotent within a board lifecycle: if the map already covers the requested
 * task ids, no network call is made. Stale entries for tasks no longer in the
 * list are dropped to prevent unbounded growth.
 *
 * @param {Array<{id: string, forgeTagsIds?: string[]}>} tasks - Tasks visible on the board.
 * @param {Object} store - Vuex store instance (used to dispatch TASK/GET_TASKS_ISOLATION_BY_IDS and read currentAssetId).
 * @returns {Promise<void>} Resolves when the map is populated.
 */
export function prefetchTasksIsolation(tasks, store) {
  // Wraps the actual work in an internal promise so that concurrent callers
  // (e.g., List.vue's mounted() and entries watcher firing on the same tick)
  // share one in-flight request.
  const promise = doPrefetchTasksIsolation(tasks, store);
  currentPrefetchPromise = promise.finally(() => {
    if (currentPrefetchPromise === promise) {
      currentPrefetchPromise = null;
    }
  });
  return currentPrefetchPromise;
}

async function doPrefetchTasksIsolation(tasks, store) {
  if (!Array.isArray(tasks) || tasks.length === 0) return;

  const persistedTasks = tasks.filter((task) => task?.id);
  if (persistedTasks.length === 0) {
    debugLog('TaskIsolation', 'prefetchTasksIsolation: no persisted tasks to prefetch');
    return;
  }

  // Facility-change cache reset (see cachedForAssetId). MUST run before the
  // "already cached" short-circuit below — otherwise an A → B switch with the
  // same task ids returns facility A's cached, facility-relative result on B.
  const currentAssetId = store.state?.assetId || null;
  if (currentAssetId !== cachedForAssetId) {
    if (cachedForAssetId !== null) {
      debugLog('TaskIsolation', 'prefetchTasksIsolation: facility changed — clearing isolation cache', {
        from: cachedForAssetId,
        to: currentAssetId,
      });
      clearIsolationCache();
    }
    cachedForAssetId = currentAssetId;
  }

  // Drop stale entries for tasks that left the board (delete/filter).
  const validIds = new Set(persistedTasks.map((task) => task.id));
  for (const cachedId of taskIsolationByTaskId.keys()) {
    if (!validIds.has(cachedId)) {
      taskIsolationByTaskId.delete(cachedId);
    }
  }

  // Only request what's missing — the map is the cache.
  const missingIds = persistedTasks
    .filter((task) => !taskIsolationByTaskId.has(task.id))
    .map((task) => task.id);

  if (missingIds.length === 0) {
    debugLog('TaskIsolation', 'prefetchTasksIsolation: all tasks already cached', {
      cachedCount: persistedTasks.length,
    });
    return;
  }

  const assetId = store.state?.assetId || null;

  debugLog('TaskIsolation', 'prefetchTasksIsolation: dispatching batch', {
    taskCount: missingIds.length,
    assetId,
  });

  try {
    const results = await store.dispatch('TASK/GET_TASKS_ISOLATION_BY_IDS', {
      taskIds: missingIds,
      assetId,
    });

    if (!Array.isArray(results)) {
      debugError('TaskIsolation', 'prefetchTasksIsolation: unexpected response shape', results);
      return;
    }

    const taskById = new Map(persistedTasks.map((task) => [task.id, task]));
    // Index by task id; the C# DTO is camelCased on the wire. EffectiveTagIds
    // and Targets preserve the context/POI plan while direct ids remain a
    // rolling-deploy fallback for older API responses.
    for (const entry of results) {
      if (entry?.taskId) {
        taskIsolationByTaskId.set(
          entry.taskId,
          normalizeTaskIsolationEntry(entry, taskById.get(entry.taskId)),
        );
      }
    }

    debugLog('TaskIsolation', 'prefetchTasksIsolation: indexed', {
      received: results.length,
      mapSize: taskIsolationByTaskId.size,
    });
  } catch (err) {
    debugError('TaskIsolation', 'prefetchTasksIsolation: dispatch failed', err);
    // Don't throw — callers should still render (fallback fetches kick in).
  }
}

/**
 * Returns the currently-in-flight prefetch promise, or a resolved promise if
 * none is running. Components mounting during a prefetch should await this
 * before reading from the map; otherwise they race-miss and fall back to the
 * legacy per-task fetch (defeating Phase 2's whole point).
 */
export function awaitInFlightPrefetch() {
  return currentPrefetchPromise || Promise.resolve();
}

/**
 * Determines click behavior based on isolation info.
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @returns {string} One of: 'none', 'isolate', 'navigate-menu'
 *
 * Behaviors:
 * - 'none': No tags or no available assets
 * - 'isolate': Tags ONLY in current facility (no other options)
 * - 'navigate-menu': Tags exist in other facilities - show selection menu
 *
 * NOTE: We always show menu when other facilities exist to let user decide.
 * The menu will include "Current Facility" option if tag is also local.
 */
export function getIsolateClickBehavior(isolationInfo) {
  if (!isolationInfo) return 'none';

  // If other facilities exist, ALWAYS show menu (let user decide)
  if (isolationInfo.otherAssets.length > 0) {
    return 'navigate-menu';
  }

  // Only if tags exist ONLY in current facility, isolate directly
  if (isolationInfo.hasCurrentFacility) {
    return 'isolate';
  }

  return 'none';
}

/**
 * Determines button color based on isolation info.
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @returns {string} Vuetify color: 'primary' or 'warning'
 */
export function getIsolateButtonColor(isolationInfo) {
  if (!isolationInfo) return 'primary';

  // Orange (warning) if tags exist in other facilities
  if (isolationInfo.otherAssets.length > 0) {
    return 'warning';
  }

  return 'primary';
}

/**
 * Picks the icon for the isolate button based on which viewer the click
 * will open. Decision tree (preferring 3D over 360 when both are available):
 *
 *   1. Tag is harvested in the current facility    → mdi-cube-outline      (3D viewer)
 *   2. Tag is 360-positioned in the current facility → mdi-image-filter-center-focus (360 viewer)
 *   3. Cross-facility, any other facility is harvested → mdi-cube-outline  (cross-facility 3D)
 *   4. Cross-facility, only 360-positioned facilities → mdi-image-filter-center-focus (cross-facility 360)
 *   5. No data / unreachable                       → mdi-loupe     (legacy fallback)
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @returns {string} Material Design Icon name (without the 'mdi-' prefix when
 *                   used in HTML strings; with the prefix when passed to v-icon).
 */
export function getIsolateIcon(isolationInfo) {
  if (!isolationInfo) return 'mdi-loupe';

  // Local-first: the click will open whatever's available HERE.
  if (isolationInfo.isHarvestedInCurrentFacility) return 'mdi-cube-outline';
  if (isolationInfo.isPositionedInCurrentFacility) return 'mdi-image-filter-center-focus';

  // Cross-facility: the click opens the facility-selection menu/dialog;
  // we still want the icon to communicate WHICH viewer the user will end up in.
  // Prefer 3D when ANY reachable other-facility has harvested data.
  const others = isolationInfo.otherAssets || [];
  const harvested = isolationInfo.harvestedAssets || [];
  const positioned = isolationInfo.positionedAssets || [];

  if (others.some((a) => harvested.includes(a))) return 'mdi-cube-outline';
  if (others.some((a) => positioned.includes(a))) return 'mdi-image-filter-center-focus';

  // Defensive fallback — shouldn't be reached given the visibility gate
  // (shouldShowIsolateButton hides the button when there's nothing to navigate to).
  return 'mdi-loupe';
}

/**
 * Generates tooltip text based on isolation behavior.
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @param {Object} facilityDict - Map of facility IDs to names
 * @returns {string} Tooltip text
 */
export function getIsolateTooltip(isolationInfo, facilityDict = {}) {
  if (!isolationInfo) return 'Show on 3D';

  const behavior = getIsolateClickBehavior(isolationInfo);

  switch (behavior) {
    case 'isolate':
      if (isolationInfo.otherAssets.length > 0) {
        return 'Isolate (right-click for other facilities)';
      }
      return 'Isolate on 3D';

    case 'navigate-single':
      return `Open in ${facilityDict[isolationInfo.otherAssets[0]] || 'other facility'}`;

    case 'navigate-menu':
      return 'Open in another facility';

    default:
      return 'Show on 3D';
  }
}

/**
 * Navigate to a facility in a new tab using the viewer chosen by the shared
 * task spatial resolver.
 *
 * Two URL contracts are honored by the canonical receiving-side handler
 * (`MainView.vue::handleTagSpatialNavigationFromUrl`):
 *   - `?tagId=X&isolate=true`   → explicit 3D viewer + isolation
 *   - `?tagId=X&tagIds=X,Y,Z&isolate=true` → the same for several tags (utils/tagDeepLink.js)
 *   - `?tagId=X&zoom360=true`   → exact 360 viewer + scan focus
 *
 * Hydrated tags are authoritative. Batched isolation summaries are adapted
 * to the same target contract; their old availability fields remain only as
 * a rolling-deploy fallback when exact hydrated targets are unavailable.
 *
 * If the receiving side ever needs another viewer kind, both ends must
 * change in sync — keep this function and the canonical MainView handler aligned.
 *
 * TODO(tagcard-360-aware-isolate): TagCard.vue may have its own call sites
 * that pass no `isolationInfo` and currently fall back to 3D. If the user
 * later reports the same bug from a TagCard, follow the same shape but
 * derive viewer from the tag's own data (children vs facilityOverrides)
 * rather than `isolationInfo` (single tag, not a multi-tag aggregate).
 *
 * @param {Object} router - Vue Router instance
 * @param {string} facilityId - Target facility ID
 * @param {string[]} tagIds - Array of tag IDs to isolate
 * @param {Object|null} [isolationInfo] - Optional aggregate from getTaskIsolationInfo;
 *   when present, used to choose between 3D and 360 URLs. When omitted,
 *   defaults to 3D — preserving the original pre-fix behavior.
 */
export function navigateToFacility(router, facilityId, tagIds, isolationInfo = null) {
  const currentFacilityId = router?.currentRoute?.params?.id
    || isolationInfo?.currentAssetId
    || null;
  const tags = taskResolutionTags(isolationInfo, tagIds);
  const plan = resolveTaskSpatialIntent(tags, {
    currentFacilityId,
    selectedFacilityId: facilityId,
    intent: 'task-focus',
  });
  const viewer = planTargetsFacility(plan, facilityId)
    ? plan.viewer
    : (getCopyLinkItemIcon(isolationInfo, facilityId) === 'mdi-image-filter-center-focus'
      ? '360'
      : '3d');
  const queryAction = viewer === '360' ? TAG_LINK_ACTION.ZOOM_360 : TAG_LINK_ACTION.ISOLATE;
  // Every tag of the task in that facility (isolate), within the link's limits (utils/tagDeepLink.js);
  // one tag, and 360, build exactly the old `?tagId=…` link.
  const location = (query) => ({ name: 'facility', params: { id: facilityId }, query });
  const query = tagLinkQuery(tagIds, queryAction, (candidate) => router.resolve(location(candidate)).href);
  if (!query) return;
  const route = router.resolve(location(query));

  debugLog('TaskIsolation', 'Navigating to facility:', {
    facilityId,
    tagIds,
    viewer,
    reason: plan.reason,
    href: route.href,
  });

  window.open(route.href, '_blank');
}

/**
 * Maps facility IDs to facility objects with names.
 *
 * @param {string[]} facilityIds - Array of facility IDs
 * @param {Object} facilityDict - Map of facility IDs to names
 * @returns {Array<{id: string, name: string}>} Array of facility objects
 */
export function mapFacilitiesToObjects(facilityIds, facilityDict = {}) {
  return facilityIds.map((id) => ({
    id,
    name: facilityDict[id] || id,
  }));
}

/**
 * Determines copy-link button click behavior. Mirrors `getIsolateClickBehavior`
 * but applies the copy-link semantics: a button is always allowed (even when
 * the task has no tags anywhere — that case becomes a direct copy of a
 * `task-board`-intent URL for the current facility).
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @returns {'none' | 'direct-copy' | 'navigate-menu'}
 *
 * Behaviors:
 * - 'none': isolationInfo is missing entirely (defensive — caller should
 *   not have rendered the button yet)
 * - 'direct-copy': single target (only current facility) — clicking writes
 *   one URL to the clipboard with no menu interaction
 * - 'navigate-menu': cross-facility — clicking opens the menu so the user
 *   chooses which facility's URL to copy
 */
export function getCopyLinkBehavior(isolationInfo) {
  if (!isolationInfo) return 'none';
  if ((isolationInfo.otherAssets || []).length > 0) return 'navigate-menu';
  return 'direct-copy';
}

/**
 * Determines copy-link button color. Identical color rule as the isolate
 * button (orange when cross-facility, blue otherwise) so users learn one
 * mental model.
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @returns {'primary' | 'warning'}
 */
export function getCopyLinkButtonColor(isolationInfo) {
  if (!isolationInfo) return 'primary';
  if ((isolationInfo.otherAssets || []).length > 0) return 'warning';
  return 'primary';
}

/**
 * Picks the menu-item icon for a given facility, based on what the recipient
 * will see when they open the link:
 *
 *   - 'mdi-cube-outline'             → 3D viewer with isolation (facility has harvested tags)
 *   - 'mdi-image-filter-center-focus' → 360 viewer with isolation (facility has positioned tags only)
 *   - 'mdi-view-dashboard'           → Kanban / task-board view (facility has no tags for this task)
 *
 * @param {Object|null} isolationInfo
 * @param {string} facilityId
 * @returns {string} Material Design Icon name (with 'mdi-' prefix)
 */
function getCopyLinkItemIcon(isolationInfo, facilityId) {
  if (!isolationInfo || !facilityId) return 'mdi-view-dashboard';
  const harvested = isolationInfo.harvestedAssets || [];
  const positioned = isolationInfo.positionedAssets || [];
  if (harvested.includes(facilityId)) return 'mdi-cube-outline';
  if (positioned.includes(facilityId)) return 'mdi-image-filter-center-focus';
  return 'mdi-view-dashboard';
}

/**
 * Builds the menu items for the cross-facility copy-link menu. The universal
 * rule (per the approved feature plan) governs each item:
 *
 *   For a facility F:
 *     If F is in combinedAssets (task has tags here)   → intent = 'task-expand'
 *     Else                                             → intent = 'task-board'
 *
 * The current facility is ALWAYS included as an item — even when the task
 * has no tags here, in which case the rule above gives it the 'task-board'
 * intent. This mirrors the isolate menu's "include current when applicable"
 * semantics but is more inclusive (we always offer to share the task-board
 * URL for the user's own facility).
 *
 * Item ordering: current first (so the most-likely target is at the top),
 * then other facilities in the order they appear in `otherAssets`.
 *
 * @param {Object|null} isolationInfo - Result from getTaskIsolationInfo
 * @param {string|null} currentAssetId - The viewer's current facility id
 * @param {Object} facilityDict - Map of facility id → human-readable name
 * @returns {Array<{ facilityId: string, facilityName: string, intent: 'task-expand' | 'task-board', icon: string }>}
 */
export function getCopyLinkMenuItems(isolationInfo, currentAssetId, facilityDict = {}) {
  if (!isolationInfo) return [];

  const combined = isolationInfo.combinedAssets || [];
  const others = isolationInfo.otherAssets || [];
  const tags = taskResolutionTags(isolationInfo);
  const hasHydratedTags = Array.isArray(isolationInfo.tags) && isolationInfo.tags.length > 0;

  // Current first (when known), then others in original order. We dedupe
  // even though others is supposed to exclude current — defense in depth.
  const orderedIds = [];
  const seen = new Set();
  if (currentAssetId) {
    orderedIds.push(currentAssetId);
    seen.add(currentAssetId);
  }
  for (const id of others) {
    if (!id || seen.has(id)) continue;
    orderedIds.push(id);
    seen.add(id);
  }

  const items = [];
  for (const facilityId of orderedIds) {
    const plan = resolveTaskSpatialIntent(tags, {
      currentFacilityId: currentAssetId,
      selectedFacilityId: facilityId,
      intent: 'task-focus',
    });
    // Batched summaries cannot carry a current-facility scan ID. Preserve
    // their server-owned availability until that response contract catches up.
    const canExpand = planTargetsFacility(plan, facilityId)
      || (!hasHydratedTags && combined.includes(facilityId));
    const intent = canExpand ? 'task-expand' : 'task-board';
    items.push({
      facilityId,
      facilityName: facilityDict[facilityId] || facilityId,
      intent,
      icon: intent === 'task-board'
        ? 'mdi-view-dashboard'
        : taskLinkIcon(plan, isolationInfo, facilityId),
    });
  }
  return items;
}

/**
 * Returns the single menu item for the direct-copy case. When
 * getCopyLinkBehavior is 'direct-copy' OR 'none', the caller uses this to
 * figure out which intent to pass to copyTaskLinkToClipboard. The single
 * target is always the current facility under the universal rule.
 *
 * Tolerates a null isolationInfo (the "task has no tags anywhere" case): the
 * universal rule still applies — combinedAssets is treated as empty, so
 * `combined.includes(currentAssetId)` is false, and we return a `task-board`
 * intent for the current facility. This means the recipient lands on the
 * Kanban view (matching the no-tags-anywhere row of the verification table)
 * instead of seeing an unhelpful "current facility unknown" error toast.
 *
 * Returns null only when `currentAssetId` itself is missing (truly broken
 * state — viewer with no facility), in which case no URL can be generated
 * and the caller surfaces the error toast.
 *
 * @param {Object|null} isolationInfo
 * @param {string|null} currentAssetId
 * @returns {{ facilityId: string, intent: 'task-expand' | 'task-board' } | null}
 */
export function getCopyLinkDirectTarget(isolationInfo, currentAssetId) {
  if (!currentAssetId) return null;
  const combined = isolationInfo?.combinedAssets || [];
  const tags = taskResolutionTags(isolationInfo);
  const plan = resolveTaskSpatialIntent(tags, {
    currentFacilityId: currentAssetId,
    selectedFacilityId: currentAssetId,
    intent: 'task-focus',
  });
  const hasHydratedTags = Array.isArray(isolationInfo?.tags) && isolationInfo.tags.length > 0;
  return {
    facilityId: currentAssetId,
    intent: (planTargetsFacility(plan, currentAssetId)
      || (!hasHydratedTags && combined.includes(currentAssetId)))
      ? 'task-expand'
      : 'task-board',
  };
}

/**
 * Clears both isolation caches (per-tag-set and per-task-id).
 * Useful when tag data might have changed (e.g., after a tag harvest update).
 */
export function clearIsolationCache() {
  isolationCache.clear();
  taskIsolationByTaskId.clear();
  debugLog('TaskIsolation', 'Isolation cache cleared (both per-tag-set and per-task-id)');
}

/**
 * Removes a specific entry from the isolation cache.
 *
 * @param {string[]} tagIds - Array of tag IDs to remove from cache
 */
export function invalidateCacheForTags(tagIds) {
  const cacheKey = [...tagIds].sort().join(',');
  isolationCache.delete(cacheKey);
  debugLog('TaskIsolation', 'Cache invalidated for tags:', cacheKey);
}
